import React, { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import store from '../../store'
import { getTickerGames } from '../../actions/actionCreators'
import { formatGamesByLeague, capitalizeFirstLetter } from 'utils/js/shared_helpers'

import TickerGame from './TickerGame'
import TickerGhost from './TickerGhost'
import TickerLeagueTitle from './TickerLeagueTitle'
import './Ticker.scss'
import useIsMobile from '../../context/IsMobileContext'
import { getDatetime } from 'utils/js/time_helpers'
import { getSeasonTypeFromGameType } from 'utils/js/nfl_helpers'

function Ticker(props) {
    const multiDayLeagues = ['cfl', 'nfl']
    //global states
    const { isMobileApp } = useIsMobile()

    /////helper functions
    const timers = {}
    const hiddenCheck = () => {
        const { week, type } = props

        // if tab is hidden, kill ticker ajax polling
        if (document.hidden) {
            ajaxDelay(
                () => {
                    getTickerGames(false)
                },
                'games',
                200
            )
        } else if (!document.hidden) {
            ajaxDelay(
                () => {
                    getTickerGames(
                        true,
                        false,
                        gameDateString.current,
                        league,
                        sport,
                        multiLeague,
                        week,
                        getSeasonTypeFromGameType(type)
                    )
                },
                'games',
                200
            )
        }
    }

    const ajaxDelay = (callback, id, ms) => {
        if (timers[id]) {
            clearTimeout(timers[id])
        }
        timers[id] = setTimeout(callback, ms)
    }

    const scrollListener = () => {
        if (window.pageYOffset > 0) {
            // Hide the ticker if we scroll down
            if (props.ticker.open) {
                store.dispatch({ type: 'TICKER_TOGGLE', tickerOpen: false })
            }
        }
    }

    const getGameDateString = timestamp => {
        let gameDate = new Date(timestamp * 1000)
        let dd = gameDate.getDate() < 10 ? '0' + gameDate.getDate() : gameDate.getDate()
        let mm =
            gameDate.getMonth() + 1 < 10 ? '0' + (gameDate.getMonth() + 1) : gameDate.getMonth() + 1
        let yyyy = gameDate.getFullYear()

        return yyyy + '-' + mm + '-' + dd
    }

    const toggleTicker = () => {
        let tickerOpen = props.ticker && props.ticker.open ? false : true
        store.dispatch({
            type: 'ANALYTIC_EVENT_LIVETRACKER_ACTION',
            json: { actionName: 'ticker expand' },
        })
        store.dispatch({ type: 'TICKER_TOGGLE', tickerOpen: tickerOpen })
    }

    const leagueMapping = (leagueName = '', sport = '', multiLeague) => {
        if (multiLeague) {
            return capitalizeFirstLetter(sport)
        } else {
            return leagueName.toUpperCase()
        }
    }

    const sortGamesByStatus = games => {
        return games.sort((gameOne, gameTwo) => {
            if (gameOne.leagueTitle) {
                return -1
            } else if (gameTwo.leagueTitle) {
                return 1
            }
            if (gameOne.game_status === 'In-Progress' && gameTwo.game_status !== 'In-Progress') {
                return -1
            } else if (
                gameTwo.game_status === 'In-Progress' &&
                gameOne.game_status !== 'In-Progress'
            ) {
                return 1
            } else if (
                gameTwo.game_status === 'In-Progress' &&
                gameOne.game_status === 'In-Progress'
            ) {
                return 0
            } else if (gameOne.game_status === 'Pre-Game' && gameTwo.game_status !== 'Pre-Game') {
                return -1
            } else if (gameTwo.game_status === 'Pre-Game' && gameOne.game_status !== 'Pre-Game') {
                return 1
            } else if (gameTwo.game_status === 'Pre-Game' && gameOne.game_status === 'Pre-Game') {
                return 0
            } else {
                return 0
            }
        })
    }

    let gameDateString = useRef(getGameDateString(props.timestamp) || ' ')
    // hook equivalences
    useEffect(() => {
        // componentDidMount logic
        const { league, sport, multiLeague, week, type } = props
        document.addEventListener('visibilitychange', hiddenCheck)
        window.addEventListener('scroll', scrollListener)

        getTickerGames(
            true,
            false,
            gameDateString.current,
            league,
            sport,
            multiLeague,
            week,
            getSeasonTypeFromGameType(type)
        )
        return () => {
            //cleaning up
            getTickerGames(false) // kill any lingering ajax polling between games
            clearTimeout(timers['games'])
            window.removeEventListener('scroll', scrollListener)
            document.removeEventListener('visibilitychange', hiddenCheck)
            store.dispatch({ type: 'TICKER_TOGGLE', tickerOpen: false })
        }
    }, [])

    const { ticker, league, sport, multiLeague } = props

    let tickerClass = ticker && ticker.open ? 'active' : 'closed'
    let mobileAppClass = isMobileApp ? 'mobileApp' : ''
    let formattedGamesList = ticker.games

    const renderTickerDay = (games, i) => {
        if (
            i === 0 ||
            getGameDateString(games[i].timestamp) !== getGameDateString(games[i - 1].timestamp)
        ) {
            return (
                <div className="ticker-spacer">
                    {getDatetime(games[i].timestamp, 'date-upper-case')}
                </div>
            )
        }
    }

    let sortedGames = sortGamesByStatus(formattedGamesList)

    if (multiLeague) {
        sortedGames = formatGamesByLeague(ticker.games, sport)
    }
    return (
        <React.Fragment>
            <div className={`ticker__cont ${tickerClass} ${mobileAppClass} visible-xs`}>
                <div className="ticker__games_cont">
                    <div className="ticker__inner_cont">
                        {sortedGames.map((game, i) => {
                            if (game.leagueTitle) {
                                return (
                                    <TickerLeagueTitle
                                        key={game.id}
                                        tickerClass={tickerClass}
                                        title={game.leagueTitle}
                                        index={i}
                                    />
                                )
                            } else {
                                return (
                                    <>
                                        {multiDayLeagues.includes(league) &&
                                            renderTickerDay(sortedGames, i)}
                                        <TickerGame
                                            key={game.id}
                                            ticker={ticker}
                                            tickerClass={tickerClass}
                                            game={game}
                                            league={league}
                                            sport={sport}
                                            index={i}
                                        />
                                    </>
                                )
                            }
                        })}
                        {formattedGamesList.length === 0 && !ticker.loading && !ticker.error && (
                            <div className="no_games">
                                No Other {leagueMapping(league, sport, multiLeague)} Games
                            </div>
                        )}
                        {ticker.loading && (
                            <div>
                                <TickerGhost />
                                <TickerGhost />
                                <TickerGhost />
                                <TickerGhost />
                                <TickerGhost />
                                <TickerGhost />
                                <TickerGhost />
                                <TickerGhost />
                            </div>
                        )}
                        {ticker.error && (
                            <div className="no_games">An Error Has Occurred. Please Stand By.</div>
                        )}
                    </div>
                </div>
            </div>
            {!isMobileApp && (
                <div
                    className={`ticker__toggler_container ${tickerClass} ${mobileAppClass} ${props.league}`}
                >
                    <div className={`ticker_toggle_space left ${tickerClass}`} />
                    <div className="ticker_toggle" onClick={toggleTicker}>
                        {leagueMapping(league, sport, multiLeague)} SCORES{' '}
                        <span className={`toggle_arrow ${tickerClass}`} />
                    </div>
                    <div className={`ticker_toggle_space right ${tickerClass}`} />
                </div>
            )}
        </React.Fragment>
    )
}

const mapStateToProps = state => {
    return {
        ticker: state.ticker,
        timestamp: state.data.hasOwnProperty('details') ? state.data.details.timestamp : 0,
        type: state.data.hasOwnProperty('details') ? state.data.details.type : 'reg',
        week: state.data.hasOwnProperty('details') ? state.data.details.week : 0,
    }
}

export default connect(mapStateToProps)(Ticker)
